import type { SaveOfferBuilderRoomParams } from '~/hooks/apis/accommodation/useSaveOfferBuilderRoom';
import type { OpportunityRoom } from '~/hooks/apis/offers/useGetOffer';

type RoomDraftPayload = SaveOfferBuilderRoomParams['room'];
type RoomBedGroup = NonNullable<RoomDraftPayload['bed_groups']>[number];
type RoomBedConfiguration = NonNullable<RoomBedGroup['configuration']>[number];

export type FlashOfferRoomDraft = RoomDraftPayload & {
  category?: string | null;
  description: string;
  draftId: string;
  existingRoomName?: string | null;
  name: string;
  property_id: string;
  room_policies?: unknown;
  shouldCreate: boolean;
  size_sqm?: number | null;
};

type EmbeddedRoomTypeSource = {
  _embedded?: {
    room_types?: Array<App.RoomType>;
  };
  room_types?: Array<App.RoomType>;
};

type SalesforceRoomSource = Pick<OpportunityRoom, 'bed_groups' | 'id' | 'name' | 'room_asset' | 'room_size__c'>;

type GeneratedBeddingConfigurationResponse = {
  result?: {
    bed_grouping?: Array<Array<{ bed_type?: string; quantity?: number }>>;
  };
};

type MapSalesforceRoomsToRoomDraftsParams = {
  propertyCategory?: string | null;
  propertyId: string;
  rooms?: Array<SalesforceRoomSource>;
};

type BuildBlankRoomDraftParams = {
  draftId: string;
  propertyCategory?: string | null;
  propertyId: string;
};

function getStringValue(value: string | number | null | undefined) {
  const trimmedValue = value === undefined || value === null ? undefined : String(value).trim();
  return trimmedValue || undefined;
}

function getRoomSizeSqm(value: string | number | null | undefined) {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? Math.round(value) : null;
  }

  const roomSizeValue = getStringValue(value);
  const matchedValue = roomSizeValue?.match(/\d+(?:\.\d+)?/)?.[0];
  const roomSize = matchedValue ? Number(matchedValue) : null;

  if (!roomSize || !Number.isFinite(roomSize)) {
    return null;
  }

  return Math.round(roomSize);
}

function getRoomAssetId(room: SalesforceRoomSource) {
  return getStringValue(room.room_asset?.sfid) ?? getStringValue(room.room_asset?.id);
}

function getRoomDraftName(room: SalesforceRoomSource) {
  return getStringValue(room.room_asset?.name) ?? '';
}

function getRoomBedGroups(sourceRooms: Array<SalesforceRoomSource>) {
  return sourceRooms.find((room) => room.bed_groups?.length)?.bed_groups ?? [];
}

function getSalesforceRoomSizeSqm(room: SalesforceRoomSource) {
  return getRoomSizeSqm(room.room_size__c) ?? getRoomSizeSqm(room.room_asset?.room_size__c);
}

function buildBedDescription(configuration: RoomBedConfiguration) {
  if (!configuration.quantity || !configuration.type) {
    return null;
  }

  const typeWithoutBed = String(configuration.type).replace('Bed', '');
  const formattedType = typeWithoutBed
    .replace('DoubleSofa', 'Double Sofa')
    .replace('QueenSofa', 'Queen Sofa')
    .replace('RollAway', 'Roll Away');

  return `${configuration.quantity} ${formattedType} Bed${configuration.quantity > 1 ? 's' : ''}`;
}

function getBedGroupDescription(bedGroup: RoomBedGroup) {
  const descriptionParts = (bedGroup.configuration ?? []).map((configuration) => buildBedDescription(configuration));
  const descriptionValues = descriptionParts.filter((description): description is string => !!description);
  const lastDescription = descriptionValues.length > 1 ? descriptionValues.pop() : null;

  return `${descriptionValues.join(', ')}${lastDescription ? ` and ${lastDescription}` : ''}`;
}

function getNumberOfBeds(bedGroups: Array<RoomBedGroup>) {
  return bedGroups.map((bedGroup) =>
    (bedGroup.configuration ?? []).reduce((total, configuration) => total + (configuration.quantity || 0), 0),
  );
}

export function normaliseRoomDraftFormData(formData: Partial<FlashOfferRoomDraft>): Partial<FlashOfferRoomDraft> {
  const bedGroups = (formData.bed_groups ?? []).map((bedGroup) => ({
    ...bedGroup,
    description: getBedGroupDescription(bedGroup),
  }));

  return {
    ...formData,
    bed_groups: bedGroups,
    ...(bedGroups.length > 0 ? { number_of_beds: getNumberOfBeds(bedGroups) } : {}),
  };
}

export function mapGeneratedBeddingConfigurationToBedGroups(
  response: GeneratedBeddingConfigurationResponse | undefined,
): NonNullable<RoomDraftPayload['bed_groups']> {
  return (
    response?.result?.bed_grouping?.map((group) => ({
      configuration: group.map((bed) => ({
        quantity: bed.quantity,
        type: bed.bed_type,
      })),
    })) ?? []
  );
}

export function getPropertyRoomTypes(property?: (App.Property & EmbeddedRoomTypeSource) | null) {
  return property?.room_types ?? property?._embedded?.room_types ?? [];
}

export function mapSalesforceRoomsToRoomDrafts({
  propertyCategory,
  propertyId,
  rooms = [],
}: MapSalesforceRoomsToRoomDraftsParams): Array<FlashOfferRoomDraft> {
  const groupedRooms = new Map<string, Array<SalesforceRoomSource>>();

  rooms.forEach((room) => {
    const roomAssetId = getRoomAssetId(room);

    if (!roomAssetId) {
      return;
    }

    const groupKey = `asset:${roomAssetId}`;
    groupedRooms.set(groupKey, [...(groupedRooms.get(groupKey) ?? []), room]);
  });

  return Array.from(groupedRooms.entries()).map(([groupKey, sourceRooms]) => {
    const firstRoom = sourceRooms[0];
    const roomName = getRoomDraftName(firstRoom);
    const roomSizeSqm = sourceRooms.map(getSalesforceRoomSizeSqm).find(Boolean) ?? null;
    const bedGroups = getRoomBedGroups(sourceRooms).map((bedGroup) => ({
      ...bedGroup,
      description: getBedGroupDescription(bedGroup),
    }));

    return {
      ...buildBlankRoomDraft({
        draftId: groupKey,
        propertyCategory,
        propertyId,
      }),
      bed_groups: bedGroups,
      name: roomName,
      number_of_beds: bedGroups.length > 0 ? getNumberOfBeds(bedGroups) : [],
      size_sqm: roomSizeSqm,
      shouldCreate: true,
    };
  });
}

export function buildBlankRoomDraft({
  draftId,
  propertyCategory,
  propertyId,
}: BuildBlankRoomDraftParams): FlashOfferRoomDraft {
  return {
    category: propertyCategory ?? null,
    description: '',
    draftId,
    existingRoomName: null,
    name: '',
    property_id: propertyId,
    shouldCreate: true,
    size_sqm: null,
    amenities: [],
    availability_threshold: null,
    bed_groups: [],
    has_live_cm_data: null,
    number_of_bathrooms: null,
    number_of_bedrooms: null,
    number_of_beds: [],
    room_type_code: null,
    source: null,
  };
}

export function getCreatableRoomDrafts(roomDrafts: Array<FlashOfferRoomDraft>) {
  return roomDrafts.filter((roomDraft) => roomDraft.shouldCreate && !roomDraft.existingRoomName);
}

export function buildRoomCreatePayload(roomDraft: FlashOfferRoomDraft, fallbackPropertyId: string): RoomDraftPayload {
  const {
    draftId: _draftId,
    existingRoomName: _existingRoomName,
    room_policies: _roomPolicies,
    shouldCreate: _shouldCreate,
    ...room
  } = roomDraft;

  return {
    ...room,
    description: room.description?.trim() ?? '',
    name: roomDraft.name.trim(),
    property_id: room.property_id || fallbackPropertyId,
  };
}
